import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Package,
  ShoppingBag,
  TrendingUp,
  Users,
  ShieldCheck,
  Zap,
  MapPin,
} from 'lucide-react';
import AppHeader from '@/components/AppHeader';
import AppFooter from '@/components/AppFooter';
import { getStoredUser } from '@/lib/authStore';
import { statsApi } from '@/lib/api';
import { formatINR } from '@/lib/format';
import { CATEGORIES } from '@/lib/constants';

function StatCard({ icon: Icon, label, value, loading }) {
  return (
    <div className="p-4 border" style={{ borderColor: 'rgba(237,232,220,0.2)', backgroundColor: 'rgba(237,232,220,0.05)' }}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} style={{ color: 'var(--primary)' }} />
        <span
          className="text-xs font-heading tracking-stamp opacity-60"
          style={{ fontFamily: 'var(--font-heading)', color: 'var(--background)' }}
        >
          {label}
        </span>
      </div>
      <p
        className="text-2xl font-heading font-mono-data"
        style={{ color: 'var(--background)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}
      >
        {loading ? '—' : value}
      </p>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }) {
  return (
    <div
      className="p-5 border-2 border-foreground"
      style={{ backgroundColor: 'var(--card)', boxShadow: '3px 3px 0 var(--muted)' }}
    >
      <div className="p-2.5 border inline-flex mb-3" style={{ borderColor: 'var(--primary)', backgroundColor: 'rgba(193,64,29,0.08)' }}>
        <Icon size={20} style={{ color: 'var(--primary)' }} />
      </div>
      <h3 className="font-heading text-base tracking-stamp mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
        {title.toUpperCase()}
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
        {description}
      </p>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    setUser(getStoredUser());

    let cancelled = false;
    statsApi
      .platform()
      .then((data) => {
        if (!cancelled) setStats(data.stats);
      })
      .catch(() => {
        // API might not be running yet — the page still works, just
        // without live numbers.
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePrimaryCta = () => {
    navigate(user ? '/dashboard' : '/login');
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--background)' }}>
      <AppHeader userName={user?.name} userRole={user?.role} currentPath="/" />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ backgroundColor: 'var(--foreground)' }}>
        <div className="absolute inset-0 ledger-grid opacity-10" />
        <div className="relative z-10 max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 border mb-6"
                style={{ borderColor: 'var(--primary)', borderStyle: 'dashed' }}
              >
                <span
                  className="text-xs font-heading tracking-stamp"
                  style={{ color: 'var(--primary)', fontFamily: 'var(--font-heading)' }}
                >
                  ELEVATE 2026 · KARNATAKA STARTUP CELL
                </span>
              </div>

              <h1 className="font-heading leading-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                <span className="block text-4xl lg:text-5xl xl:text-6xl tracking-stamp" style={{ color: 'var(--background)' }}>
                  CLEAR DEADSTOCK.
                </span>
                <span className="block text-4xl lg:text-5xl xl:text-6xl tracking-stamp" style={{ color: 'var(--primary)' }}>
                  UNLOCK VALUE.
                </span>
              </h1>

              <p className="text-base lg:text-lg leading-relaxed mb-8 max-w-xl" style={{ color: 'rgba(237,232,220,0.75)' }}>
                India's B2B & B2C deadstock clearance marketplace. Sellers post surplus
                inventory in minutes; buyers across the country discover deals and
                order directly — no middlemen, no wasted stock.
              </p>

              <div className="flex flex-wrap gap-3 mb-10">
                <button onClick={handlePrimaryCta} className="btn-primary px-6 py-3 text-sm flex items-center gap-2">
                  {user ? 'GO TO DASHBOARD' : 'GET STARTED FREE'}
                  <ArrowRight size={15} />
                </button>
                <Link to="/marketplace" className="btn-secondary px-6 py-3 text-sm flex items-center gap-2">
                  <ShoppingBag size={15} />
                  BROWSE MARKETPLACE
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard icon={Users} label="USERS" value={stats?.totalUsers ?? 0} loading={statsLoading} />
                <StatCard icon={TrendingUp} label="GMV" value={formatINR(stats?.gmv ?? 0)} loading={statsLoading} />
                <StatCard icon={Package} label="DEALS CLOSED" value={stats?.dealsClosed ?? 0} loading={statsLoading} />
                <StatCard icon={ShoppingBag} label="LISTINGS" value={stats?.activeListings ?? 0} loading={statsLoading} />
              </div>
            </div>

            <div className="lg:col-span-2">
              <div
                className="paper-texture border-2 border-foreground p-6"
                style={{ boxShadow: '5px 5px 0 var(--primary)' }}
              >
                <p className="text-xs font-heading tracking-stamp mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>
                  HOW IT WORKS
                </p>
                {[
                  { step: '01', title: 'List or Browse', body: 'Sellers post surplus stock; buyers search by category and city.' },
                  { step: '02', title: 'Place an Order', body: 'Buyers order directly at the listed unit price — no negotiation loop.' },
                  { step: '03', title: 'Track to Delivery', body: 'Sellers move orders through confirmed → in transit → delivered.' },
                ].map((s) => (
                  <div key={s.step} className="flex gap-3 py-3 border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
                    <span
                      className="font-heading text-lg"
                      style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}
                    >
                      {s.step}
                    </span>
                    <div>
                      <p className="text-sm font-medium mb-0.5">{s.title}</p>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-screen-2xl mx-auto w-full px-4 lg:px-8 xl:px-10 2xl:px-16 py-14">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1 h-6" style={{ backgroundColor: 'var(--primary)' }} />
            <h2 className="font-heading text-2xl tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
              BUILT FOR B2B & B2C CLEARANCE
            </h2>
          </div>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            Everything sellers and buyers need to move surplus inventory — nothing they don't.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <FeatureCard
            icon={Zap}
            title="List in minutes"
            description="Post a listing with photos, quantity, and price — live on the marketplace instantly."
          />
          <FeatureCard
            icon={ShieldCheck}
            title="Verified companies"
            description="Every seller is a registered business with a company name and city on every listing."
          />
          <FeatureCard
            icon={TrendingUp}
            title="Track every deal"
            description="A live dashboard follows each order from confirmation through delivery."
          />
          <FeatureCard
            icon={MapPin}
            title="Pan-India reach"
            description="Filter by city and category to find (or be found by) buyers and sellers nationwide."
          />
        </div>
      </section>

      {/* Categories */}
      <section className="py-14" style={{ backgroundColor: 'var(--card)', borderTop: '2px solid var(--foreground)', borderBottom: '2px solid var(--foreground)' }}>
        <div className="max-w-screen-2xl mx-auto w-full px-4 lg:px-8 xl:px-10 2xl:px-16">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-1 h-6" style={{ backgroundColor: 'var(--primary)' }} />
            <h2 className="font-heading text-2xl tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
              SHOP BY CATEGORY
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <Link
                key={cat}
                to={`/marketplace?category=${encodeURIComponent(cat)}`}
                className="px-4 py-2 text-sm border-2 border-foreground hover:opacity-80 transition-opacity"
                style={{ backgroundColor: 'var(--background)', fontFamily: 'var(--font-heading)' }}
              >
                {cat.toUpperCase()}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-screen-2xl mx-auto w-full px-4 lg:px-8 xl:px-10 2xl:px-16 py-16">
        <div
          className="border-2 border-foreground p-8 lg:p-12 flex flex-col lg:flex-row items-center justify-between gap-6"
          style={{ backgroundColor: 'var(--foreground)', boxShadow: '5px 5px 0 var(--primary)' }}
        >
          <div>
            <h2
              className="font-heading text-2xl lg:text-3xl tracking-stamp mb-2"
              style={{ color: 'var(--background)', fontFamily: 'var(--font-heading)' }}
            >
              READY TO CLEAR YOUR SURPLUS?
            </h2>
            <p className="text-sm" style={{ color: 'rgba(237,232,220,0.7)' }}>
              Join sellers and buyers already trading deadstock on ClearanceConnect.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/login?tab=register&role=seller" className="btn-primary px-6 py-3 text-sm flex items-center gap-2">
              <Package size={15} />
              REGISTER AS SELLER
            </Link>
            <Link to="/login?tab=register&role=buyer" className="btn-secondary px-6 py-3 text-sm flex items-center gap-2">
              <ShoppingBag size={15} />
              REGISTER AS BUYER
            </Link>
          </div>
        </div>
      </section>

      <AppFooter />
    </div>
  );
}
